import { useEffect, type RefObject } from "react";

/**
 * Гарантия видимости поля над системной клавиатурой (A46-коррекция 2026-08-12:
 * имя клиента и комментарий ДК вводятся клавиатурой ОС, и точка печати обязана
 * оставаться видимой НАД ней).
 *
 * Хук вешается ТОЧЕЧНО на поле (ref-based), не глобально. Механика:
 *  - visualViewport resize + scroll (с debounce — клавиатура «доезжает» серией
 *    событий) → перекрытие = window.innerHeight − vv.height − vv.offsetTop.
 *    iOS Safari двигает layout viewport (offsetTop ≠ 0), Android Chrome режет
 *    visual (height ↓) — формула покрывает оба поведения.
 *  - Скролл-контейнеру модалки (ближайший overflow-y предок; НЕ document.body)
 *    даётся inline padding-bottom = перекрытию, чтобы даже нижнее поле
 *    физически могло уехать выше клавиатуры.
 *  - Пока поле в фокусе — контейнер докручивается так, чтобы низ поля был выше
 *    клавиатуры с запасом GAP; при доборе высоты клавиатуры докрутка повторяется.
 *  - Рост самого поля (AutoGrowTextarea: комментарий растёт от ввода до
 *    maxHeight) не даёт НИ ОДНОГО vv-события — за размером поля следит
 *    ResizeObserver и повторяет докрутку через тот же debounce.
 *  - Pinch-zoom (vv.scale > 1) уменьшает vv.height без всякой клавиатуры —
 *    в зуме перекрытие не считаем (иначе фантомный паддинг и рывок скролла).
 *  - На blur ничего не дёргаем: клавиатура прячется → vv-resize сам снимает
 *    паддинг, скролл не трогаем (никаких скачков).
 *  - Тап ВНЕ поля при фокусе = blur (решение 13.08: «любой клик вне клавиатуры
 *    и комментария скрывает клавиатуру») — pointerdown-capture по документу;
 *    тап в другое СИС-поле (input/textarea без readonly) не трогаем: браузер
 *    штатно перефокусирует, наш blur вызвал бы лишний скачок вьюпорта.
 *  - Нет visualViewport (старые webview) — фолбэк на window-resize: там
 *    клавиатура ужимает сам layout viewport, перекрытия нет — нужна только
 *    докрутка поля в видимую зону.
 */

/** Запас между полем и краем видимой зоны (A46-коррекция: «~12px»). */
const GAP = 12;
/** Дебаунс шквала viewport-событий во время анимации клавиатуры. */
const DEBOUNCE_MS = 64;

/** Ближайший скроллируемый предок поля (скролл-тело модалки). */
function findScroller(el: HTMLElement): HTMLElement | null {
  for (let n = el.parentElement; n; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if (oy === "auto" || oy === "scroll") return n;
  }
  return null;
}

/**
 * Паддинг-компенсация клавиатуры на скроллере. Идемпотентна для нескольких
 * полей на одном скроллере: базовый (css) padding-bottom запоминается в
 * dataset при первом вмешательстве и восстанавливается при закрытии.
 */
function padScroller(scroller: HTMLElement, kb: number): void {
  if (scroller.dataset.kbBasePad === undefined) {
    if (kb <= 1) return; // клавиатуры нет и паддинг не ставили — не трогаем
    scroller.dataset.kbBasePad = String(parseFloat(getComputedStyle(scroller).paddingBottom) || 0);
  }
  if (kb > 1) {
    scroller.style.paddingBottom = `${Number(scroller.dataset.kbBasePad) + kb}px`;
  } else {
    scroller.style.paddingBottom = "";
    delete scroller.dataset.kbBasePad;
  }
}

export function useKeyboardViewportFix(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const field = ref.current;
    if (typeof window === "undefined" || !field) return;
    const vv = window.visualViewport;
    let scroller: HTMLElement | null = null;
    let timer = 0;

    const apply = () => {
      scroller ??= findScroller(field);
      if (!scroller) return;
      /* Высота перекрытия клавиатурой (0 — если layout сам ужался: фолбэк).
         При pinch-zoom (scale > 1) vv.height ужимается без клавиатуры — не считаем. */
      const kb = vv && vv.scale <= 1.01
        ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
        : 0;
      padScroller(scroller, kb);
      if (document.activeElement !== field) return; // держим только «своё» поле
      const viewBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const rect = field.getBoundingClientRect();
      /* Докрутка: низ поля выше клавиатуры с запасом, но верх поля не выше
         верха скроллера (разросшийся комментарий не утаскиваем за шапку). */
      const delta = Math.min(
        rect.bottom - (viewBottom - GAP),
        rect.top - (scroller.getBoundingClientRect().top + GAP),
      );
      if (delta > 0) scroller.scrollTop += delta;
    };

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(apply, DEBOUNCE_MS);
    };
    const onFocus = () => apply(); // мгновенная докрутка ещё до подъёма клавиатуры

    /* 13.08: тап вне поля при фокусе = blur → клавиатура ОС прячется.
       Другое редактируемое поле не блёрим — штатный перефокус без скачка. */
    const onDocDown = (e: globalThis.PointerEvent) => {
      if (document.activeElement !== field) return;
      const t = e.target;
      if (!(t instanceof Node) || field.contains(t)) return;
      const editable = t instanceof HTMLElement
        && (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement)
        && !(t as HTMLInputElement | HTMLTextAreaElement).readOnly;
      if (!editable) field.blur();
    };
    document.addEventListener("pointerdown", onDocDown, true);

    /* Рост поля от ввода (AutoGrowTextarea) не рождает vv-событий — без
       наблюдателя каретка уезжает под клавиатуру при наборе многострочного
       текста. Первичное срабатывание observer'а при монтировании безвредно:
       apply без фокуса и клавиатуры — no-op, дебаунс schedule гасит шквал. */
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    ro?.observe(field);

    field.addEventListener("focus", onFocus);
    vv?.addEventListener("resize", schedule);
    vv?.addEventListener("scroll", schedule);
    window.addEventListener("resize", schedule); // фолбэк для webview без vv
    return () => {
      window.clearTimeout(timer);
      ro?.disconnect();
      document.removeEventListener("pointerdown", onDocDown, true);
      field.removeEventListener("focus", onFocus);
      vv?.removeEventListener("resize", schedule);
      vv?.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (scroller) padScroller(scroller, 0);
    };
  }, [ref]);
}
